import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "components/ui/input";
import { Search, AlertTriangle } from "lucide-react";
import { getVehicleDetails, VehicleDetails, ValidityDetail } from "../api/shipments";
import { useBreadcrumbs } from "contexts/BreadcrumbsContext";
import { formatDate } from "lib/utils";

const Field = ({ label, value }: { label: string; value?: string | number | boolean | null }) => {
    if (value === null || value === undefined || value === "") return null;
    return (
        <div>
            <p className="text-xs text-gray-500 mb-0.5">{label}</p>
            <p className="text-sm font-medium text-gray-800">{String(value)}</p>
        </div>
    );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{title}</h4>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">{children}</div>
    </div>
);

const ValidityBadge = ({ validity }: { validity: ValidityDetail }) => {
    const today = new Date();
    const expiryDate = validity.date ? new Date(validity.date) : null;
    const daysUntilExpiry = expiryDate
        ? Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
        : null;

    let colorClass = "bg-green-50 border-green-200";
    let labelColor = "text-green-800";
    let dateColor = "text-green-600";
    if (daysUntilExpiry !== null && daysUntilExpiry < 0) {
        colorClass = "bg-red-50 border-red-200";
        labelColor = "text-red-800";
        dateColor = "text-red-600";
    } else if (daysUntilExpiry !== null && daysUntilExpiry <= 30) {
        colorClass = "bg-yellow-50 border-yellow-200";
        labelColor = "text-yellow-800";
        dateColor = "text-yellow-600";
    }

    return (
        <div className={`border rounded-lg px-3 py-2.5 ${colorClass}`}>
            <div className="flex items-start justify-between gap-2">
                <p className={`text-xs font-semibold leading-tight ${labelColor}`}>{validity.validity_type}</p>
                <p className={`text-xs font-medium whitespace-nowrap ${dateColor}`}>
                    {validity.date ? formatDate(validity.date) : "—"}
                </p>
            </div>
            {validity.details && (
                <p className={`text-xs mt-1 opacity-60 ${labelColor}`}>{validity.details}</p>
            )}
        </div>
    );
};

const VehicleDetailsPage = () => {
    const { setBreadcrumbs } = useBreadcrumbs();
    const [searchParams, setSearchParams] = useSearchParams();

    const [searchQuery, setSearchQuery] = useState<string>("");
    const [debouncedQuery, setDebouncedQuery] = useState<string>("");
    const [vehicleData, setVehicleData] = useState<VehicleDetails | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [notFound, setNotFound] = useState<boolean>(false);

    useEffect(() => {
        const vehicleFromUrl = searchParams.get("vehicleNumber");
        if (vehicleFromUrl) setSearchQuery(vehicleFromUrl);
    }, []);

    useEffect(() => {
        setBreadcrumbs([
            { label: "Reports", href: "/reports/sim-consent" },
            { label: "Vehicle Details" }
        ]);
    }, [setBreadcrumbs]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(searchQuery), 500);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        if (searchQuery) setSearchParams({ vehicleNumber: searchQuery });
        else setSearchParams({});
    }, [searchQuery, setSearchParams]);

    useEffect(() => {
        if (!debouncedQuery) {
            setVehicleData(null);
            setNotFound(false);
            return;
        }
        const fetchVehicle = async () => {
            try {
                setLoading(true);
                setNotFound(false);
                setVehicleData(await getVehicleDetails(debouncedQuery));
            } catch (err: any) {
                console.error("Vehicle fetch failed", err);
                setVehicleData(null);
                if (err?.status === 404) {
                    setNotFound(true);
                }
            } finally {
                setLoading(false);
            }
        };
        fetchVehicle();
    }, [debouncedQuery]);

    return (
        <div className="px-6 py-2 space-y-6">
            <div className="relative w-fit">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                    placeholder="Search Vehicle"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-8 w-[220px] pl-8 text-xs font-normal text-gray-600"
                />
            </div>

            {loading && <p className="text-sm text-gray-500">Loading...</p>}

            {!loading && !vehicleData && !searchQuery && (
                <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
                    <img
                        src="/images/truck.png"
                        alt="Truck"
                        className="w-28 opacity-70 mb-6"
                    />
                    <h3 className="text-lg font-semibold text-gray-800">
                        Search for a Vehicle
                    </h3>
                    <p className="text-sm text-gray-500 mt-1 max-w-sm">
                        Enter a vehicle number above to view its registration,
                        ownership, and validity details.
                    </p>
                </div>
            )}
            {!loading && notFound && debouncedQuery && searchQuery && (
                <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
                    <img
                        src="/images/truck.png"
                        alt="No Results"
                        className="w-28 opacity-60 mb-6"
                    />
                    <h3 className="text-lg font-semibold text-gray-800">
                        No Results Found
                    </h3>
                    <p className="text-sm text-gray-500 mt-1 max-w-sm">
                        We couldn’t find any vehicle details for{" "}
                        <span className="font-medium text-gray-700">
                            {searchQuery}
                        </span>.
                        <br />
                        Please verify the vehicle number and try again.
                    </p>
                </div>
            )}

            {vehicleData && (
                <div className="space-y-4 max-w-4xl">
                    {vehicleData.blacklist_status && (
                        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                            <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-semibold text-red-700">Blacklisted Vehicle</p>
                                <p className="text-xs text-red-600 mt-0.5">{vehicleData.blacklist_status}</p>
                            </div>
                        </div>
                    )}

                    {/* Header: owner + make/model */}
                    <div>
                        <p className="text-base font-semibold text-gray-900">
                        Make: {vehicleData.make}
                        </p>
                        <p className="text-base font-semibold text-gray-900">
                        Model: {vehicleData.model}
                        </p>
                        <p className="text-sm text-gray-700 mt-0.5">
                            Owner: {vehicleData.owner_name ?? "—"}
                        </p>
                    </div>

                    {/* Two-column section layout */}
                    <div className="grid grid-cols-2 gap-4">
                        <Section title="Vehicle Details">
                            <Field label="Body Type" value={vehicleData.body_type} />
                            <Field label="Category" value={vehicleData.category_description} />
                            <Field label="Fuel Type" value={vehicleData.fuel_type} />
                            {vehicleData.norms && vehicleData.norms !== "Not Available" && (
                                <Field label="Emission Norms" value={vehicleData.norms} />
                            )}
                        </Section>

                        <Section title="Registration Details">
                            <Field label="Chassis No." value={vehicleData.chasis_number} />
                            <Field label="Engine No." value={vehicleData.engine_number} />
                            <Field label="Manufactured Date" value={vehicleData.manufactured_date ? formatDate(vehicleData.manufactured_date) : undefined} />
                            <Field label="Registration Date" value={vehicleData.registration_date ? formatDate(vehicleData.registration_date) : undefined} />
                            <div className="col-span-2">
                                <Field label="Registered At" value={vehicleData.registration_at} />
                            </div>
                            {vehicleData.registration_status && (
                                <div className="col-span-2">
                                    <p className="text-xs text-gray-500 mb-0.5">Status</p>
                                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                                        vehicleData.registration_status === "ACTIVE"
                                            ? "bg-green-100 text-green-700"
                                            : "bg-red-100 text-red-700"
                                    }`}>
                                        {vehicleData.registration_status}
                                    </span>
                                </div>
                            )}
                        </Section>

                        <Section title="Weight & Dimensions">
                            <Field label="Unladen Weight" value={vehicleData.unladen_weight ? `${vehicleData.unladen_weight.toLocaleString()} kg` : undefined} />
                            <Field label="Gross Weight" value={vehicleData.gross_weight ? `${vehicleData.gross_weight.toLocaleString()} kg` : undefined} />
                            <Field label="Loading Capacity" value={vehicleData.loading_capacity ? `${vehicleData.loading_capacity.toLocaleString()} kg` : undefined} />
                            <Field label="Wheel Base" value={vehicleData.wheel_base ? `${vehicleData.wheel_base.toLocaleString()} mm` : undefined} />
                        </Section>

                        <Section title="Financing">
                            <Field label="Financed" value={vehicleData.is_financed ? "Yes" : "No"} />
                            {vehicleData.is_financed && (
                                <Field label="Financier" value={vehicleData.financier_name} />
                            )}
                        </Section>
                    </div>

                    {vehicleData.validity_details?.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Validity Details</h4>
                            <div className="grid grid-cols-2 gap-2">
                                {vehicleData.validity_details.map((v, i) => (
                                    <ValidityBadge key={i} validity={v} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default VehicleDetailsPage;
